import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Button as Btn, Typography } from "@material-ui/core";
import { Spinner } from "../../administration";
import { BlobProvider } from "@react-pdf/renderer";
import ApiService from "../../service/ApiService";
import AlertService from "../../service/AlertService";
import EstimatePreview from "../../administration/estimate/EstimatePreview";
import Estimate from "../../model/estimate";
import Pathnames from "../../model/enum/Pathnames";
import LogoIcon from "../../assets/main.png";
import DownloadIcon from "../../assets/icons/others/download.svg";
import PhoneIcon from "../../assets/icons/forms/phone.svg";
import MailIcon from "../../assets/icons/forms/mail.svg";
import GlobeIcon from "../../assets/icons/forms/globe.svg";
import useStyles from "./styles";

const EstimatePreviewDocument = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const code = searchParams.get("code");
  const token = searchParams.get("token");
  const [loading, setLoading] = useState(true);
  const [estimate, setEstimate] = useState<Estimate | null>();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!code || !token) {
      AlertService.error("Missing code or token in URL parameters");
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      const queryParams = new URLSearchParams();
      queryParams.append("code", code);
      queryParams.append("token", token);

      await ApiService.get<Estimate>(
        `frontend/estimate/preview?${queryParams.toString()}`
      )
        .then((data) => {
          setEstimate(data);
        })
        .catch((error) => {
          console.error("Error fetching estimate data:", error);
          setEstimate(null);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    fetchData();
  }, [code, token]);

  const handleSubmit = async (accepted: boolean) => {
    if (!estimate) {
      AlertService.error("Estimate data is not available.");
      return;
    }

    if (accepted && (estimate.Status?.id === 20 || estimate.approved)) {
      AlertService.info("Estimate already approved. Thank you!");
      return;
    }

    if (!accepted && estimate.Status?.id === 21) {
      AlertService.info("Estimate already declined. Thank you!");
      return;
    }

    try {
      setLoading(true);
      const response = await ApiService.put<Estimate>(
        `frontend/estimate/approval?code=${estimate.code}&token=${estimate.previewToken}`,
        {
          approved: accepted,
          approvedAt: accepted ? new Date() : null,
          statusId: accepted ? 20 : 21,
        }
      );
      if (response) {
        if (response.approved) {
          AlertService.success("Estimate accepted successfully!");
        } else {
          AlertService.success("Estimate declined successfully!");
        }
      } else {
        AlertService.error(
          "Failed to accept the estimate. Please try again later."
        );
      }
    } catch (error) {
      AlertService.error("Error updating the estimate.");
      console.error("Error accepting estimate:", error);
    } finally {
      setLoading(false);
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    }
  };

  return (
    <>
      {/** Main div */}
      {loading && <Spinner />}
      {estimate ? (
        <div className={classes.estimateContainer}>
          {/** Big div */}
          <div className={classes.estimateTotalCard}>
            <img src={LogoIcon} className={classes.logo} alt="logo" />
            <Typography className={classes.totalText}>
              Estimate Total
            </Typography>
            <Typography className={classes.totalValue}>
              {`$${estimate?.total},00`}
            </Typography>
            <Typography style={{ width: "70%" }}>
              To move forward with estimate, select <span style={{textDecoration: "underline"}}>Accept</span>
              {" "}and we will reach out with next steps.
            </Typography>
            <div className={classes.buttonDiv}>
              <Btn
                className={classes.acceptButton}
                onClick={() => handleSubmit(true)}
              >
                Accept
              </Btn>
              <Btn
                className={classes.declineButton}
                onClick={() => handleSubmit(false)}
              >
                Decline
              </Btn>
            </div>
            <hr style={{ width: "100%" }} />
            <Typography className={classes.infoText}>
              Estimates are subject to change if needed. The business will reach
              out to confirm final details.
            </Typography>
          </div>
          {/** 2 small divs */}
          <div className={classes.detailContainer}>
            {/** top right */}
            <div className={classes.estimateDetailCard}>
              <Typography className={classes.brandText}>
                Setup Remodeling Inc.
              </Typography>
              <div className="d-flex col w-100 mt-2 justify-content-between">
                <label className={classes.greyText}>Estimate code</label>
                <Typography className={classes.boldText}>
                  {estimate?.code}
                </Typography>
              </div>
              <div className="d-flex col w-100 mt-2 justify-content-between">
                <label className={classes.greyText}>Estimate total</label>
                <Typography className={classes.boldText}>
                  {`$${estimate?.total},00`}
                </Typography>
              </div>
              <div className="d-flex col w-100 mt-2 justify-content-end">
                <div className={classes.downloadInfoDiv}>
                  <Btn
                    href={pdfUrl ?? ""}
                    download={`estimate_${estimate.code}.pdf`}
                    disabled={!pdfUrl}
                  >
                    <img src={DownloadIcon} alt="download icon" />
                    <Typography className={classes.greyText}>
                      Download
                    </Typography>
                  </Btn>
                </div>
              </div>
              {estimate && (
                <BlobProvider
                  document={<EstimatePreview estimate={estimate} />}
                >
                  {({ url, loading }) => {
                    if (loading) return <Spinner />;
                    if (!pdfUrl) setPdfUrl(url);
                    return (
                      <div className={classes.buttonDiv}>
                        <Btn
                          className={classes.declineButton}
                          href={url || ""}
                          target="_blank"
                          disabled={loading}
                        >
                          View estimate
                        </Btn>
                      </div>
                    );
                  }}
                </BlobProvider>
              )}
            </div>
            {/** bottom right */}
            <div className={classes.estimateDetailCard}>
              <Typography className={classes.brandText}>Our Contact</Typography>
              <div className="d-flex col w-100 align-items-center">
                <img
                  src={PhoneIcon}
                  alt="Phone Icon"
                  style={{ marginRight: 5 }}
                />
                <a href="tel:+1-800-530-4099" style={{ color: "inherit" }}>
                  <Typography>+1 (800)-530-4099</Typography>
                </a>
              </div>
              <div className="d-flex col w-100 align-items-center">
                <img
                  src={MailIcon}
                  alt="Email Icon"
                  style={{ marginRight: 5 }}
                />
                <a
                  href="mailto:adm@setupremodeling.com"
                  style={{ color: "inherit" }}
                >
                  <Typography>adm@setupremodeling.com</Typography>
                </a>
              </div>
              <div className="d-flex col w-100 align-items-center">
                <img
                  src={GlobeIcon}
                  alt="Website Icon"
                  style={{ marginRight: 5 }}
                />
                <a href={Pathnames.HOMEPAGE} style={{ color: "inherit" }}>
                  <Typography>setupremodeling.com</Typography>
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={classes.noEstimateContainer}>
          <Typography variant="h5">
            No estimate data found for the provided code.
          </Typography>
          <Btn
            className={classes.backButton}
            onClick={() => navigate(Pathnames.HOMEPAGE)}
          >
            Go Back to Homepage
          </Btn>
        </div>
      )}
      {/** Toast container for notifications */}
      <ToastContainer />
    </>
  );
};

export default EstimatePreviewDocument;
